import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { TopicModal } from '@/components/TopicModal';
import { DeleteConfirmModal } from '@/components/DeleteConfirmModal';
import { DraggableList } from '@/components/DraggableList';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Trash2, Edit, ChevronRight, Loader2 } from 'lucide-react';
import { subjectsApi, topicsApi } from '@/services/api';

export default function Topics() {
  const { subjectId } = useParams();
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubjectId, setSelectedSubjectId] = useState(subjectId || '');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);

  const currentSubject = subjects.find(s => s._id === selectedSubjectId);

  useEffect(() => {
    loadSubjects();
  }, []);

  useEffect(() => {
    if (selectedSubjectId) {
      loadTopics();
    } else {
      setTopics([]);
    }
  }, [selectedSubjectId]);

  const loadSubjects = async () => {
    try {
      const res = await subjectsApi.getAll();
      setSubjects(res.data || []);
      if (!selectedSubjectId && res.data?.length > 0) {
        setSelectedSubjectId(subjectId || res.data[0]._id);
      }
    } catch (error) {
      toast.error('Dersler yüklenemedi');
    }
  };

  const loadTopics = async () => {
    if (!selectedSubjectId) return;
    try {
      setLoading(true);
      const res = await topicsApi.getBySubject(selectedSubjectId);
      setTopics(res.data || []);
    } catch (error) {
      toast.error('Konular yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const filteredTopics = useMemo(() => {
    if (!search) return topics;
    return topics.filter(t => 
      t.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [topics, search]);

  const handleSave = async (data) => {
    try {
      if (editingTopic) {
        await topicsApi.update(editingTopic._id, data);
        toast.success('Konu güncellendi');
      } else {
        await topicsApi.create(data);
        toast.success('Konu oluşturuldu');
      }
      setModalOpen(false);
      setEditingTopic(null);
      loadTopics();
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleDelete = async () => {
    try {
      await Promise.all(selected.map(id => topicsApi.delete(id)));
      toast.success(`${selected.length} konu silindi`);
      setSelected([]);
      setDeleteModalOpen(false);
      loadTopics();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const handleReorder = async (newItems) => {
    setTopics(newItems);
    try {
      await Promise.all(
        newItems.map((item, index) => 
          topicsApi.update(item._id, { order: index })
        )
      );
      toast.success('Sıralama güncellendi');
    } catch (error) {
      toast.error('Sıralama kaydedilemedi');
      loadTopics();
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selected.length === filteredTopics.length) {
      setSelected([]);
    } else {
      setSelected(filteredTopics.map(t => t._id));
    }
  };

  const breadcrumbs = [
    { label: 'Dersler', href: '/subjects' },
    ...(currentSubject ? [{ label: currentSubject.name, href: `/subjects/${currentSubject._id}/topics` }] : []),
    { label: 'Konular' },
  ];

  const renderTopicItem = (topic) => (
    <Card 
      className="hover:border-teal-200 transition-colors"
      data-testid={`topic-item-${topic._id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <Checkbox
            checked={selected.includes(topic._id)}
            onCheckedChange={() => toggleSelect(topic._id)}
            data-testid={`select-topic-${topic._id}`}
          />
          <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
            <span className="text-teal-700 font-bold">{topic.order ?? topics.indexOf(topic) + 1}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 truncate">{topic.name}-{topic._id}</h3>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => { setEditingTopic(topic); setModalOpen(true); }}
              data-testid={`edit-topic-${topic._id}`}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Link to={`/topics/${topic._id}/questions`}>
              <Button variant="ghost" size="icon" data-testid={`view-questions-${topic._id}`}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout breadcrumbs={breadcrumbs}>
      <div className="space-y-6" data-testid="topics-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Konular
            </h1>
            <p className="text-slate-500">Ders konularını yönetin</p>
          </div>
          <Button 
            onClick={() => { setEditingTopic(null); setModalOpen(true); }}
            className="bg-teal-700 hover:bg-teal-800"
            disabled={!selectedSubjectId}
            data-testid="add-topic-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Yeni Konu
          </Button>
        </div>

        {/* Subject Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Select value={selectedSubjectId} onValueChange={setSelectedSubjectId}>
            <SelectTrigger className="w-full sm:w-64" data-testid="subject-filter">
              <SelectValue placeholder="Ders seçin" />
            </SelectTrigger>
            <SelectContent>
              {subjects.map((subject) => (
                <SelectItem key={subject._id} value={subject._id}>
                  {subject.icon} {subject.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Konu ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-topics"
            />
          </div>

          {selected.length > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-500">{selected.length} seçili</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteModalOpen(true)}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="bulk-delete-topics-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Sil
              </Button>
            </div>
          )}
        </div>

        {/* Select All */}
        {filteredTopics.length > 0 && (
          <div className="flex items-center gap-2">
            <Checkbox
              checked={selected.length === filteredTopics.length && filteredTopics.length > 0}
              onCheckedChange={toggleSelectAll}
              data-testid="select-all-topics"
            />
            <span className="text-sm text-slate-500">Tümünü seç</span>
          </div>
        )}

        {/* Content */}
        {!selectedSubjectId ? (
          <Card className="p-12 text-center">
            <p className="text-slate-500">Lütfen bir ders seçin</p>
          </Card>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        ) : filteredTopics.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-lg font-semibold text-slate-900">Henüz konu yok</h3>
            <p className="text-slate-500 mt-1">Bu derse yeni bir konu ekleyin</p>
            <Button 
              onClick={() => setModalOpen(true)}
              className="mt-4 bg-teal-700 hover:bg-teal-800"
            >
              <Plus className="w-4 h-4 mr-2" /> İlk Konuyu Ekle
            </Button>
          </Card>
        ) : (
          <DraggableList
            items={filteredTopics}
            onReorder={handleReorder}
            renderItem={renderTopicItem}
            disabled={search.length > 0}
          />
        )}
      </div>

      <TopicModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingTopic(null); }}
        onSave={handleSave}
        topic={editingTopic}
        subjectId={selectedSubjectId}
      />

      <DeleteConfirmModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        count={selected.length}
      />
    </Layout>
  );
}
